import { useEffect, useId, useRef, useState } from 'react'

import { api } from '../lib/api'
import type { AddressSuggestion, AutocompleteResponse } from '../lib/types'

import { Field, inputClass } from './Field'

/**
 * Address autocomplete over the API's Photon proxy (D35, 01 §3.2).
 *
 * Three things this must get right:
 *  - **Attribution.** ODbL requires it and it is rendered whenever suggestions are shown.
 *  - **Typing must always win.** Suggestions are a shortcut, never a gate: someone whose
 *    address OpenStreetMap does not know still has to be able to book a visit, so the fields
 *    stay editable and a geocoder outage silently degrades to a plain form.
 *  - **Keyboard.** Arrow keys, Enter, Escape, and a live region announcing the result count
 *    (01 §8, WCAG 2.2 AA) — a combobox that only works with a mouse is not accessible.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  error,
}: {
  value: string
  onChange: (value: string) => void
  onSelect: (suggestion: AddressSuggestion) => void
  error?: string
}) {
  const id = useId()
  const listId = `${id}-listbox`
  const [results, setResults] = useState<AddressSuggestion[]>([])
  const [attribution, setAttribution] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const skipNextLookup = useRef(false)

  useEffect(() => {
    if (skipNextLookup.current) {
      skipNextLookup.current = false
      return
    }
    if (value.trim().length < 3) {
      setResults([])
      return
    }
    // 250 ms: long enough that a typed address is a handful of requests rather than one per
    // keystroke, short enough that suggestions feel immediate (D35 asks for polite usage).
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get<AutocompleteResponse>(
          `/geocode/autocomplete?q=${encodeURIComponent(value)}`,
        )
        setResults(response.results)
        setAttribution(response.attribution)
        setOpen(response.results.length > 0)
        setActive(-1)
      } catch {
        setResults([])
        setOpen(false)
      }
    }, 250)
    return () => window.clearTimeout(timer)
  }, [value])

  function choose(suggestion: AddressSuggestion) {
    skipNextLookup.current = true
    onSelect(suggestion)
    setOpen(false)
    setResults([])
  }

  return (
    <div className="relative">
      <Field
        id={id}
        label="Property address"
        hint="Start typing and pick your address, or just type it in."
        error={error}
        required
      >
        {(props) => (
          <input
            {...props}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
            autoComplete="street-address"
            className={inputClass}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (!open || results.length === 0) return
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setActive((i) => (i + 1) % results.length)
              } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                setActive((i) => (i - 1 + results.length) % results.length)
              } else if (event.key === 'Enter' && active >= 0) {
                event.preventDefault()
                const picked = results[active]
                if (picked) choose(picked)
              } else if (event.key === 'Escape') {
                setOpen(false)
              }
            }}
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          />
        )}
      </Field>

      <p aria-live="polite" className="sr-only">
        {open ? `${results.length} address suggestions` : ''}
      </p>

      {open && results.length > 0 && (
        <div className="absolute inset-x-0 top-full z-20 mt-1 overflow-hidden rounded-card border border-line bg-white shadow-lift">
          <ul id={listId} role="listbox" className="m-0 max-h-72 list-none overflow-auto p-0">
            {results.map((suggestion, index) => (
              <li
                key={suggestion.label}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                onMouseDown={(event) => {
                  event.preventDefault()
                  choose(suggestion)
                }}
                onMouseEnter={() => setActive(index)}
                className={`cursor-pointer px-3.5 py-2.5 text-sm ${
                  index === active ? 'bg-blue-tint text-ink' : 'text-body'
                }`}
              >
                {suggestion.label}
                {!suggestion.in_service_area && (
                  <span className="ml-2 text-xs text-muted">outside Monmouth County</span>
                )}
              </li>
            ))}
          </ul>
          {attribution && (
            <p className="m-0 border-t border-line bg-paper px-3.5 py-2 text-xs text-muted">
              {attribution}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
